import { useMemo } from 'react';
import type { Store, Session } from '../services/types';
import { OnboardingStep } from '../services/types';
import { useIsOnboardingPath } from './useIsOnboardingPath';

interface UseIsOnboardingOptions {
  /** Store configuration */
  store?: Store;
  /** Current session */
  currentSession?: Session;
  /** Current onboarding step */
  onboardingStep?: OnboardingStep;
  /** Current search ID */
  searchId: string | null;
  /** URL search query parameter */
  urlSearchQuery?: string;
}

/**
 * Custom hook to determine if onboarding is currently active.
 *
 * Onboarding is active when ALL of these are true:
 * - Pathname starts with "onboarding" (allows /onboarding or onboarding)
 * - Onboarding is enabled in store configuration
 * - Session exists
 * - No active search context exists
 * - Onboarding flow is not completed
 *
 * This hook memoizes the onboarding state calculation to avoid unnecessary recalculations.
 *
 * @param options - Required parameters for onboarding state calculation
 * @returns boolean indicating if onboarding is active
 *
 * @example
 * ```tsx
 * const { store, currentSession } = useGetStoreInfo();
 * const { onboardingStep, searchId, urlSearchQuery } = useChatContext();
 *
 * const isOnboarding = useIsOnboarding({
 *   store,
 *   currentSession,
 *   onboardingStep,
 *   searchId,
 *   urlSearchQuery,
 * });
 * ```
 */
export function useIsOnboarding({
  store,
  currentSession,
  onboardingStep,
  searchId,
  urlSearchQuery,
}: UseIsOnboardingOptions): boolean {
  const isOnboardingPath = useIsOnboardingPath();

  return useMemo(() => {
    // Pathname must start with "onboarding" (allows optional leading slash)
    if (!isOnboardingPath) {
      return false;
    }

    // Must have an active session
    if (!currentSession?.id) {
      return false;
    }

    // Skip if user already has search context
    if (searchId || urlSearchQuery) {
      return false;
    }

    // Skip if onboarding is completed
    if (onboardingStep === OnboardingStep.WRAP_UP) {
      return false;
    }

    // Check if onboarding is enabled in store configuration
    const hasOnboardingEnabled =
      store?.config?.config.moduleEnabled?.includes('onboarding') ?? false;

    return hasOnboardingEnabled;
  }, [
    isOnboardingPath,
    currentSession,
    searchId,
    urlSearchQuery,
    onboardingStep,
    store,
  ]);
}
